import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Footer from "../../../examples/Footer";
import axios from "axios";

/**
 * Student Transfer / Rejoin Report
 * - Fetches list from: /api/student-reports/:clientId/:collegeId/transfer
 * - CSV: /api/student-reports/:clientId/:collegeId/download/transfer/csv
 * - PDF: /api/student-reports/:clientId/:collegeId/download/transfer/pdf
 */
export default function StudentTransferOrRejoin() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [batches, setBatches] = useState([]);
  const [branches, setBranches] = useState([]);

  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedAdmissionTypes, setSelectedAdmissionTypes] = useState([]);
  const [yearFilter, setYearFilter] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const user = useMemo(() => {
    const userData = sessionStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  }, []);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchBranches = useCallback(async () => {
    if (!user) return;
    try {
      const resp = await axios.get(
        `${API_BASE_URL}/api/branches/${user.client_id}/${user.college_id}`,
        { headers: getAuthHeaders() }
      );
      if (resp.data && resp.data.data && Array.isArray(resp.data.data)) setBranches(resp.data.data);
      else setBranches([]);
    } catch (err) {
      console.error("Error fetching branches", err);
      setBranches([]);
    }
  }, [user]);

  const fetchBatches = useCallback(async () => {
    if (!user) return;
    try {
      const branchQuery = selectedBranch ? `?branchId=${selectedBranch}` : "";
      const url = `${API_BASE_URL}/api/admittedbatches/${user.client_id}/${user.college_id}${branchQuery}`;
      const resp = await axios.get(url, { headers: getAuthHeaders() });
      if (Array.isArray(resp.data)) setBatches(resp.data);
      else if (resp.data && Array.isArray(resp.data.data)) setBatches(resp.data.data);
      else setBatches([]);
    } catch (err) {
      console.error("Error fetching batches", err);
      setBatches([]);
    }
  }, [user, selectedBranch]);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search && search.trim() !== "") params.append("search", search.trim());
      if (selectedBatch) params.append("admittedBatchId", selectedBatch);
      if (selectedBranch) params.append("branchId", selectedBranch);
      if (selectedAdmissionTypes && selectedAdmissionTypes.length > 0)
        params.append("admissionType", selectedAdmissionTypes.join(","));
      if (yearFilter) params.append("year", yearFilter);

      const queryString = params.toString();
      const baseUrl = `${API_BASE_URL}/api/student-reports/${user.client_id}/${user.college_id}/transfer`;
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const resp = await axios.get(url, { headers: getAuthHeaders() });
      // Debug: log raw response so we can see the payload shape in the browser console
      // eslint-disable-next-line no-console
      console.log("StudentTransferOrRejoinReport fetch resp:", resp?.data);
      let data = [];
      if (Array.isArray(resp.data)) data = resp.data;
      else if (resp.data && Array.isArray(resp.data.data)) data = resp.data.data;

      const formatted = data.map((r, i) => ({ id: r.student_id || r.id || i, ...r }));
      setRows(formatted);

      if (!formatted.length) {
        showSnackbar("No transfer/rejoin records found for the selected filters", "info");
      }
    } catch (err) {
      console.error("Error fetching transfer/rejoin data", err);
      showSnackbar("Error fetching data", "error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user, search, selectedBatch, selectedBranch, selectedAdmissionTypes, yearFilter]);

  const handleDownloadCsv = async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams();
      if (selectedBranch) params.append("branchId", selectedBranch);
      if (selectedBatch) params.append("admittedBatchId", selectedBatch);
      if (selectedAdmissionTypes && selectedAdmissionTypes.length > 0)
        params.append("admissionType", selectedAdmissionTypes.join(","));
      if (yearFilter) params.append("year", yearFilter);
      const queryString = params.toString();
      const baseUrl = `${API_BASE_URL}/api/student-reports/${user.client_id}/${user.college_id}/download/transfer/csv`;
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const response = await axios.get(url, { headers: getAuthHeaders(), responseType: "blob" });
      const blob = new Blob([response.data], { type: "text/csv" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "student_transfer_rejoin.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showSnackbar("CSV downloaded successfully", "success");
    } catch (err) {
      console.error("Error downloading CSV", err);
      showSnackbar("Error downloading CSV", "error");
    }
  };

  const handleDownloadPdf = async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams();
      if (selectedBranch) params.append("branchId", selectedBranch);
      if (selectedBatch) params.append("admittedBatchId", selectedBatch);
      if (selectedAdmissionTypes && selectedAdmissionTypes.length > 0)
        params.append("admissionType", selectedAdmissionTypes.join(","));
      if (yearFilter) params.append("year", yearFilter);
      const queryString = params.toString();
      const baseUrl = `${API_BASE_URL}/api/student-reports/${user.client_id}/${user.college_id}/download/transfer/pdf`;
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const response = await axios.get(url, { headers: getAuthHeaders(), responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "student_transfer_rejoin.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showSnackbar("PDF downloaded successfully", "success");
    } catch (err) {
      console.error("Error downloading PDF", err);
      showSnackbar("Error downloading PDF", "error");
    }
  };

  useEffect(() => {
    if (user) {
      fetchBranches();
      fetchData();
    }
  }, [user, fetchBranches, fetchData]);

  useEffect(() => {
    if (user) fetchBatches();
  }, [user, selectedBranch, fetchBatches]);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleFilterClick = (e) => setFilterAnchorEl(e.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const toggleAdmissionType = (t) =>
    setSelectedAdmissionTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );

  const clearAll = () => {
    setSelectedBranch("");
    setSelectedBatch("");
    setSelectedAdmissionTypes([]);
    setYearFilter("");
    setSearch("");
  };

  const admissionTypes = ["TRANSFER", "REJOIN"];

  const columns = [
    { field: "admission_no", headerName: "Admission No", flex: 1, minWidth: 140 },
    { field: "student_name", headerName: "Student Name", flex: 1.5, minWidth: 160 },
    {
      field: "original_batch",
      headerName: "Original Batch",
      flex: 1.2,
      minWidth: 140,
      valueGetter: (p) =>
        p.row.original_batch_id || p.row.original_batch || p.row.originalBatch || "",
    },
    {
      field: "rejoin_batch",
      headerName: "Rejoin Batch",
      flex: 1.2,
      minWidth: 140,
      valueGetter: (p) =>
        p.row.rejoin_batch_id ||
        p.row.rejoin_batch ||
        p.row.rejoinBatch ||
        p.row.batch?.admittedbatch ||
        "",
    },
    { field: "admission_type", headerName: "Admission Type", flex: 0.9, minWidth: 120 },
    { field: "transfer_credits", headerName: "Transfer Credits", flex: 0.7, minWidth: 110 },
    {
      field: "transfer_date",
      headerName: "Transfer Date",
      flex: 0.9,
      minWidth: 120,
      valueGetter: (p) => (p.row.transfer_date ? String(p.row.transfer_date).split("T")[0] : ""),
    },
    { field: "break_reason", headerName: "Break Reason", flex: 1, minWidth: 160 },
  ];

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            STUDENT TRANSFER / REJOIN REPORT
          </Typography>

          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            onClick={handleMenuOpen}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#115293" },
            }}
          >
            ACTIONS
          </Button>

          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleFilterClick}
            sx={{
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                borderColor: "#115293",
                color: "#115293",
              },
            }}
          >
            FILTERS
            {(selectedBatch || selectedBranch || selectedAdmissionTypes.length || yearFilter) && (
              <Chip
                size="small"
                label={
                  (selectedBatch ? 1 : 0) +
                  (selectedBranch ? 1 : 0) +
                  (selectedAdmissionTypes ? selectedAdmissionTypes.length : 0) +
                  (yearFilter ? 1 : 0)
                }
                sx={{ ml: 1, backgroundColor: "#1976d2", color: "white" }}
              />
            )}
          </Button>

          <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  handleMenuClose();
                  handleDownloadCsv();
                }}
                sx={{ backgroundColor: "#4caf50", color: "#fff" }}
              >
                DOWNLOAD CSV
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => {
                  handleMenuClose();
                  handleDownloadPdf();
                }}
                sx={{ backgroundColor: "#e53935", color: "#fff" }}
              >
                DOWNLOAD PDF
              </Button>
            </MenuItem>
          </Menu>

          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={handleFilterClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                background: "#ffffff",
                minWidth: 240,
                maxWidth: 320,
              },
            }}
          >
            <Box sx={{ p: 1.5, background: "#ffffff" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                  pb: 1,
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#1976d2",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  }}
                >
                  Filters
                </Typography>
                <Button
                  size="small"
                  onClick={clearAll}
                  sx={{
                    color: "#d32f2f",
                    fontSize: "0.75rem",
                    minWidth: "auto",
                    p: 0.5,
                    "&:hover": { backgroundColor: "#ffebee" },
                  }}
                >
                  Clear
                </Button>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Branch
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedBranch || ""}
                  onChange={(e) => {
                    setSelectedBranch(e.target.value);
                    setSelectedBatch("");
                  }}
                  SelectProps={{ native: true }}
                  sx={{ backgroundColor: "#f8f9fa", borderRadius: 0.5 }}
                >
                  <option value="">-- All Branches --</option>
                  {branches.map((b) => (
                    <option key={b.branch_id} value={b.branch_id}>
                      {b.branch_name}
                    </option>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Admitted Batch
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedBatch || ""}
                  onChange={(e) => setSelectedBatch(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ backgroundColor: "#f8f9fa", borderRadius: 0.5 }}
                >
                  <option value="">-- All Batches --</option>
                  {batches.map((batch) => (
                    <option key={batch.admittedbatch_id} value={batch.admittedbatch_id}>
                      {batch.admittedbatch}
                    </option>
                  ))}
                </TextField>
              </Box>

              <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Admission Type
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: 0.5,
                    border: "1px solid #e9ecef",
                    maxHeight: 120,
                    overflowY: "auto",
                  }}
                >
                  <List dense sx={{ py: 0 }}>
                    {admissionTypes.map((t) => (
                      <ListItem key={t} disablePadding>
                        <ListItemButton
                          onClick={() => toggleAdmissionType(t)}
                          sx={{ py: 0.25, px: 0.5, minHeight: 28 }}
                        >
                          <Checkbox
                            edge="start"
                            checked={selectedAdmissionTypes.includes(t)}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                            sx={{ color: "#1976d2", p: 0.25 }}
                          />
                          <ListItemText
                            primary={t}
                            sx={{
                              "& .MuiListItemText-primary": {
                                fontSize: "0.8rem",
                                color: "#555",
                                fontWeight: selectedAdmissionTypes.includes(t) ? 600 : 400,
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>

              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Year
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. 2023"
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                />
              </Box>
            </Box>
          </Popover>

          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={fetchData}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>

        <Box sx={{ height: 560, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      <Footer />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
